import { gql } from '@apollo/client';

/**
 * Shared GraphQL documents + types for the admin Quests pages — the pre-feeds keystone
 * pattern: one fragment, every document interpolates it, both pages import from here.
 */

export interface AdminQuestAudience {
  intents?: string[] | null;
  careerStages?: string[] | null;
  countryCodes?: string[] | null;
}

export interface AdminQuestExclusions {
  signedUpBefore?: string | null;
  maxVotesCast?: number | null;
  maxCommentsWritten?: number | null;
  maxPostsRead?: number | null;
  minAccountAgeDays?: number | null;
  requireTier2Activated?: boolean | null;
  minReputationPoints?: number | null;
  profileCompleteness?: ProfileCompletenessTarget | null;
}

/** 'complete' = only members whose profile meter reads 100%; 'incomplete' = only those below it. */
export type ProfileCompletenessTarget = 'complete' | 'incomplete';

export interface AdminQuestStep {
  id: string;
  stepCode: string;
  position: number;
  weight: number;
  enabled: boolean;
  configOverrides: Record<string, string | number> | null;
}

export interface AdminQuest {
  id: string;
  name: string;
  slug: string;
  heading: string;
  /** What the award card prints. Null = the quest name. */
  awardTitle: string | null;
  status: 'DRAFT' | 'LIVE' | 'ARCHIVED';
  audience: AdminQuestAudience | null;
  exclusions: AdminQuestExclusions | null;
  priority: number;
  gateStepCode: string | null;
  pointsReward: number;
  questType: string;
  emoji: string | null;
  color: string | null;
  assignment: 'auto' | 'optin' | 'invite';
  directoryVisible: boolean;
  cadence: 'once' | 'daily' | 'weekly' | 'monthly';
  channelNotify: boolean;
  channelPin: boolean;
  channelEmail: boolean;
  notifyReminder: string | null;
  emailReminder: string | null;
  inviteToken: string | null;
  audienceCount: number;
  audienceTotal: number;
  publishedAt: string | null;
  createdAt: string;
  steps: AdminQuestStep[];
}

export interface QuestStepCatalogEntry {
  code: string;
  adminLabel: string;
  adminMeasurement: string;
  completion: string;
  scope: string;
  defaultEvidence: string;
  defaultHref: string;
  defaultLabel: string;
  defaultWhy: string;
  defaultMinutes: number | null;
  defaultOpens: string;
}

export const QUEST_FIELDS = gql`
  fragment AdminQuestFields on Quest {
    id
    name
    slug
    heading
    awardTitle
    status
    audience {
      intents
      careerStages
      countryCodes
    }
    exclusions {
      signedUpBefore
      maxVotesCast
      maxCommentsWritten
      maxPostsRead
    }
    priority
    gateStepCode
    pointsReward
    # Aliased: the schema field is 'type' (a field must not repeat its parent type's
    # name), while every reader here has always called it questType.
    questType: type
    emoji
    color
    assignment
    directoryVisible
    cadence
    channelNotify
    channelPin
    channelEmail
    notifyReminder
    emailReminder
    inviteToken
    audienceCount
    audienceTotal
    publishedAt
    createdAt
    steps {
      id
      stepCode
      position
      weight
      enabled
      configOverrides
    }
  }
`;

export const ADMIN_QUESTS = gql`
  ${QUEST_FIELDS}
  query AdminQuests {
    adminQuests {
      ...AdminQuestFields
    }
  }
`;

export const ADMIN_QUEST = gql`
  ${QUEST_FIELDS}
  query AdminQuest($id: ID!) {
    adminQuest(id: $id) {
      ...AdminQuestFields
    }
  }
`;

export const ADMIN_QUEST_STEP_CATALOG = gql`
  query AdminQuestStepCatalog {
    adminQuestStepCatalog {
      code
      adminLabel
      adminMeasurement
      completion
      scope
      defaultEvidence
      defaultHref
      defaultLabel
      defaultWhy
      defaultMinutes
      defaultOpens
    }
  }
`;

export const ADMIN_CREATE_QUEST = gql`
  ${QUEST_FIELDS}
  mutation AdminCreateQuest($input: AdminCreateQuestInput!) {
    adminCreateQuest(input: $input) {
      ...AdminQuestFields
    }
  }
`;

export const ADMIN_UPDATE_QUEST = gql`
  ${QUEST_FIELDS}
  mutation AdminUpdateQuest($id: ID!, $input: AdminUpdateQuestInput!) {
    adminUpdateQuest(id: $id, input: $input) {
      ...AdminQuestFields
    }
  }
`;

export const ADMIN_SET_QUEST_STATUS = gql`
  ${QUEST_FIELDS}
  mutation AdminSetQuestStatus($id: ID!, $status: QuestStatus!) {
    adminSetQuestStatus(id: $id, status: $status) {
      ...AdminQuestFields
    }
  }
`;

export const ADMIN_SAVE_QUEST_STEPS = gql`
  ${QUEST_FIELDS}
  mutation AdminSaveQuestSteps($questId: ID!, $steps: [QuestStepInput!]!) {
    adminSaveQuestSteps(questId: $questId, steps: $steps) {
      ...AdminQuestFields
    }
  }
`;

export const ADMIN_INVITE_TO_QUEST = gql`
  mutation AdminInviteToQuest($id: ID!, $userIds: [ID!]!) {
    adminInviteToQuest(id: $id, userIds: $userIds)
  }
`;

export const ADMIN_ROTATE_QUEST_INVITE = gql`
  mutation AdminRotateQuestInvite($id: ID!) {
    adminRotateQuestInvite(id: $id)
  }
`;

/** An invite-only quest's shareable link. `questInvite`, because `invite` is the referral hash. */
export function questInviteUrl(slug: string, token: string): string {
  return `${townSquareBase()}/quests/${slug}?questInvite=${encodeURIComponent(token)}&join=1`;
}

export const STATUS_PILL_CLASSES: Record<AdminQuest['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  LIVE: 'bg-green-100 text-green-800',
  ARCHIVED: 'bg-amber-100 text-amber-800',
};

/** One-line human summary of who a quest targets; empty audience = the default quest. */
export function audienceSummary(quest: Pick<AdminQuest, 'audience'>): string {
  const audience = quest.audience;
  const parts: string[] = [];
  if (audience?.intents?.length) parts.push(`intents: ${audience.intents.join(', ')}`);
  if (audience?.careerStages?.length) parts.push(`stages: ${audience.careerStages.join(', ')}`);
  if (audience?.countryCodes?.length) parts.push(`countries: ${audience.countryCodes.join(', ')}`);
  return parts.length ? parts.join(' · ') : 'Default quest (everyone unmatched)';
}

export function listToText(list: string[] | null | undefined): string {
  return (list ?? []).join(', ');
}

export function textToList(text: string): string[] {
  return text
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

/** The shareable opt-in URL for a quest — the link that gets mass-emailed. */
export function questOptInUrl(slug: string): string {
  return `${townSquareBase()}/quests/${slug}?join=1`;
}

/** The member-facing landing page for a quest — live for members; drafts preview for admins. */
export function questLandingUrl(slug: string): string {
  return `${townSquareBase()}/quests/${slug}`;
}

/** The member-facing quest index. */
export function questsIndexUrl(): string {
  return `${townSquareBase()}/quests`;
}

/**
 * The admin step harness. It lives in town-square because everything a step can open —
 * the pickers, the Arty panel, the artwork modal — is a town-square component, so a copy
 * under /admin could not actually exercise them.
 */
export function stepHarnessUrl(): string {
  return `${townSquareBase()}/quests/steps`;
}

function townSquareBase(): string {
  return typeof __TOWN_SQUARE_URL__ !== 'undefined' ? __TOWN_SQUARE_URL__ : '';
}
